<template>
  <cst-page-template :hide-menu="true">
    <fixed-bar>
      <dashboard-quote-bar :quote-id="$route.params.quoteId"></dashboard-quote-bar>
    </fixed-bar>
    <div class="o-row vcr-page">
      <div class="o-column">
        <loader v-if="loading" size="large" />

        <template v-else-if="vcrAutolineFormat">
          <vcr-autoline-format />

          <div class="button--actions">
            <app-button data-qa="print-vcr-button" @click="printVcr">{{
              i18n.t('/Vcr/Signature/Print Label')
            }}</app-button>
          </div>
        </template>

        <template v-else>
          <h1>{{ i18n.t('/Vcr/Title') }}</h1>

          <vcr-quote-meta-info />

          <vcr-quote-contact-details />

          <vcr-financing-details />

          <div class="html2pdf__page-break" />
          <vcr-configuration-details />

          <vcr-data-table
            :title="i18n.t('/Vcr/Accessory Tables/Control Codes/Title')"
            :items="controlCodes"
            :code-field-name="'ShortId'"
          />

          <vcr-accessory-tables />

          <div class="html2pdf__page-break" />
          <vcr-exchange-car />

          <vcr-data-table
            :title="i18n.t('/Vcr/Accessory Tables/Bonus/Title')"
            :items="accessoriesWithCategories('Bonuses')"
          />

          <vcr-data-table
            :title="i18n.t('/Vcr/Accessory Tables/Undeductable Cost/Title')"
            :items="accessoriesWithCategories('NonRecoverableCosts')"
          />

          <vcr-proposal-table />

          <div class="html2pdf__page-break" />
          <vcr-signature />

          <div class="button--actions">
            <app-button data-qa="print-vcr-button" @click="printVcr">{{
              i18n.t('/Vcr/Signature/Print Label')
            }}</app-button>
          </div>
        </template>
      </div>
    </div>
  </cst-page-template>
</template>

<script>
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import QuoteApi from 'api-client/Quote.api'
import VcrQuoteMetaInfo from '@/components/generic/presentational/organisms/vcr/VcrQuoteMetaInfo.vue'
import VcrQuoteContactDetails from '@/components/generic/presentational/organisms/vcr/VcrQuoteContactDetails.vue'
import VcrFinancingDetails from '@/components/generic/presentational/organisms/vcr/VcrFinancingDetails.vue'
import VcrConfigurationDetails from '@/components/generic/presentational/organisms/vcr/VcrConfigurationDetails.vue'
import VcrAccessoryTables from '@/components/generic/presentational/organisms/vcr/VcrAccessoryTables.vue'
import VcrExchangeCar from '@/components/generic/presentational/organisms/vcr/VcrExchangeCar.vue'
import VcrDataTable from '@/components/generic/presentational/organisms/vcr/VcrDataTable.vue'
import VcrProposalTable from '@/components/generic/presentational/organisms/vcr/VcrProposalTable.vue'
import VcrSignature from '@/components/generic/presentational/organisms/vcr/VcrSignature.vue'
import VcrAutolineFormat from '@/components/generic/presentational/organisms/vcr/autolineFormat/VcrAutolineFormat.vue'
import { mapGetters } from 'vuex'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import FixedBar from '@/components/generic/presentational/atoms/fixedBar/FixedBar.vue'
import DashboardQuoteBar from '@/components/generic/presentational/molecules/dashboardQuoteBar/DashboardQuoteBar.vue'
import PdfApi from '@/api/server/Pdf.api'
import { saveAs } from 'file-saver'
import { EventBus } from '@/plugins/event-bus'

export default {
  components: {
    CstPageTemplate,
    VcrQuoteMetaInfo,
    VcrQuoteContactDetails,
    VcrFinancingDetails,
    VcrConfigurationDetails,
    VcrAccessoryTables,
    VcrExchangeCar,
    VcrDataTable,
    VcrProposalTable,
    VcrSignature,
    VcrAutolineFormat,
    Loader,
    AppButton,
    FixedBar,
    DashboardQuoteBar,
  },
  emits: ['loaded'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      loading: true,
    }
  },
  computed: {
    ...mapGetters({
      accessoriesWithCategories: 'quote/accessoriesWithCategories',
      isStockCar: 'quote/isStockCar',
      deliveryDate: 'quote/deliveryDate',
      quoteCar: 'quote/car',
      controlCodes: 'quote/controlCodes',
      vcrAutolineFormat: 'user/vcrAutolineFormat',
    }),
  },
  created() {
    //todo: shared logic w/ CustomerConfigurationPage, thus duplicate code
    QuoteApi.getQuoteById(this.$route.params.quoteId).then(async (data) => {
      this.quote = data
      this.$store.dispatch('quote/loadQuote', this.quote).then(async () => {
        if (this.quoteCar.Type === 'CONFIGURATION') {
          await this.$store.dispatch('configurationExtended/getConfigurationExtended', {
            configurationCode: this.quoteCar.ConfigurationCode,
            deliveryDate: this.deliveryDate,
          })

          await this.$store.dispatch('accessories/getForConfigurationCode', {
            configurationCode: this.quoteCar.ConfigurationCode,
          })

          await this.$store.dispatch('controlCodes/getQuoteOrderControlCodesForConfigurationCode', {
            configurationCode: this.quoteCar.ConfigurationCode,
            deliveryDate: this.deliveryDate,
          })
        } else if (this.quoteCar.Type === 'STOCKCAR') {
          await this.$store.dispatch('configurationExtended/getStockCarExtended', {
            stockCarId: this.quoteCar.StockCarId,
            deliveryDate: this.deliveryDate,
          })

          await this.$store.dispatch('accessories/getForStockCar', {
            stockCarId: this.quoteCar.StockCarId,
          })

          await this.$store.dispatch('controlCodes/getQuoteOrderControlCodesForStockCar', {
            stockCarId: this.quoteCar.StockCarId,
            deliveryDate: this.deliveryDate,
          })
        } else if (this.quoteCar.Type === 'STOCKCAR_V2') {
          await this.$store.dispatch('configurationExtended/getNewStockExtended', {
            stockId: this.quoteCar.StockId,
            deliveryDate: this.deliveryDate,
            extendedConfiguration: true,
          })

          await this.$store.dispatch('accessories/getForStock', {
            stockId: this.quoteCar.StockId,
          })

          await this.$store.dispatch('controlCodes/getQuoteOrderControlCodesForStock', {
            stockId: this.quoteCar.StockId,
            deliveryDate: this.deliveryDate,
          })
        }
        this.finishedLoading()
      })
    })
  },
  beforeUnmount() {
    this.$store.dispatch('quote/resetQuote')
    this.$store.dispatch('configuration/resetConfiguration')
    this.$store.dispatch('accessories/reset')
  },
  methods: {
    getHtml() {
      if (typeof this.$el.getHTML !== 'function') {
        const errorMsg = 'Browser does not support getHTML API.'
        console.error(errorMsg)
        throw new Error(errorMsg)
      }

      return this.$el.getHTML()
    },
    async printVcr() {
      this.loading = true

      try {
        let html = this.getHtml()
        const data = await PdfApi.GenerateHtmlToPdf(html, 'vcr-pagina', 'VcrPage')

        saveAs(data.pdfContent, data.fileName)
      } catch (e) {
        EventBus.$emit('notification-warning', 'Niet gelukt om de Pdf te downloaden')
      } finally {
        this.loading = false
      }
    },
    finishedLoading() {
      this.loading = false
      this.$emit('loaded')
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.vcr-page) {
  color: #000000;
}

.vcr-page {
  margin-top: 100px;

  :deep(&__header) {
    color: var(--color-primary);
    border-bottom: 1px solid var(--color-border);
    padding: var(--space-xxs);
    font-size: 1.8em;
  }

  :deep(&__sub-header) {
    font-size: 1.4em;
  }

  :deep(&__table-first-column) {
    width: 50%;
  }
}

.button--actions {
  display: flex;
  justify-content: flex-end;
}
</style>
